/**
 * Param plumbing for as-configurator: input IDs, the as-input-change handler,
 * and the configurator-to-inputs value sync.
 *
 * These need the configurator object but none of the element's own state, so
 * they live here as free functions and as-configurator keeps thin wrappers.
 */
import { devlog } from '../../util/utils.ts';
import { internalData } from '../../store/store.ts';
import * as ParamateUndo from './lib/paramateUndoManager.ts';
import {
  getRegisteredParamNames,
  resolveParamPending,
  applyParamConstraints,
} from '../../store/paramInputRegistry.ts';
import { lookupParam } from '../../store/paramStore.ts';

const autoIdCounter: Record<string, number> = {};

/** Assign a stable ID to an input element that lacks one. */
export function ensureInputId(el: Element): void {
  if (el.id) return;
  const label = el.getAttribute('label-default-text') || el.getAttribute('default-text');
  if (label) {
    el.id = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  } else {
    const tag = el.tagName.toLowerCase();
    autoIdCounter[tag] = (autoIdCounter[tag] ?? 0) + 1;
    el.id = `${tag}-auto-${autoIdCounter[tag]}`;
  }
}

/** Records an input's new value in the store, and its history action when the
 *  input does not manage its own. */
export function handleParamInputChange(ev: Event): void {
  const el = ev.target as any;
  if (!el) return;
  // Chrome outside the project, such as the export settings.
  if (el.hasAttribute?.('ignore-reset')) return;
  if (!el.id) ensureInputId(el);
  const id = el.id;
  if (!id) return;

  // `value` means the same thing on every AsInputBase input now — a checkbox
  // reports its checked boolean, not the form value string it used to.
  const value = el.value !== undefined ? el.value : (el as any).checked;
  const valueBefore = internalData.uiInputs[id] ?? null;

  // Ignore no-op changes to avoid duplicate history entries from redundant events
  if (value === valueBefore) {
    return;
  }

  const isParam = el.hasAttribute && el.hasAttribute('trcad-param');
  const isAutoConfig = el.hasAttribute && el.hasAttribute('configure-on-change');

  // Elements using HistoryTrackingMixin self-push their own history actions.
  // Only push here for elements that don't manage their own history (e.g. as-value-list).
  const ignoreHistory = el.hasAttribute && el.hasAttribute('ignore-history');
  if (!el._historyManaged && !ignoreHistory && (!isParam || !isAutoConfig)) {
    ParamateUndo.recordInputChange(id, valueBefore, value, el.tagName === 'AS-FILE-UPLOAD' && !!value);
  }

  internalData.uiInputs = { ...internalData.uiInputs, [id]: value };
}

/**
 * Sync param values from configurator to all registered inputs.
 * Used both on initial load and after configure.
 */
export function syncParamValuesToInputs(configurator: any): void {
  const registeredParams = getRegisteredParamNames();
  const types = registeredParams.map(key => configurator?.params?.[key]?.type);
  devlog('[AS-CONFIGURATOR] Syncing params. Registered:', registeredParams, 'types:', types);

  for (const paramName of registeredParams) {
    const paramData = lookupParam(configurator.params, paramName);

    if (paramData !== undefined) {
      const groundTruthValue = paramData.value ?? paramData.default;

      // Special handling for files (scalar `file` and `file[]` arrays):
      // don't resolve to empty if we have a local token/handle that hasn't
      // synced to ground-truth yet.
      const isFileParam = paramData.type === 'file'
        || (paramData.type === 'array' && paramData.elementType === 'file');
      const isEmptyFileValue = groundTruthValue === '' || groundTruthValue === null
        || (Array.isArray(groundTruthValue) && groundTruthValue.every((v: any) => !v));
      if (isFileParam && isEmptyFileValue) {
        const el = document.querySelector(`[trcad-param="${paramName}"]`) as any;
        if (el?.value || el?.hasLocalFile) {
          devlog(`[AS-CONFIGURATOR] Skipping resolve for ${paramName} - local token or handle exists`);
          continue;
        }
      }

      resolveParamPending(paramName, groundTruthValue);
      applyParamConstraints(paramName, paramData);
    } else {
      console.warn(`[AS-CONFIGURATOR] No param data found for ${paramName}`);
    }
  }

  // Snapshot all input element values into the store
  const snapshot: Record<string, any> = {};
  const inputEls = document.querySelectorAll('as-numbox, as-string-input, as-checkbox, as-dropdown, as-value-list, as-file-input');
  for (const el of inputEls) {
    if (el.hasAttribute('ignore-reset')) continue;
    ensureInputId(el);
    const key = el.id;
    if (el.tagName === 'AS-CHECKBOX') {
      snapshot[key] = (el as any).checked ?? false;
    } else if (el.tagName === 'AS-FILE-INPUT') {
      // Prioritize local token/handle to prevent resets during configure race
      const localVal = (el as any).value;
      if (localVal) {
        snapshot[key] = localVal;
      } else {
        const paramName = el.getAttribute('trcad-param');
        const fullName = paramName?.startsWith('main:') ? paramName : `main:${paramName}`;
        snapshot[key] = configurator.params[fullName]?.value ?? '';
      }
    } else {
      snapshot[key] = (el as any).value ?? null;
    }
  }
  internalData.uiInputs = snapshot;
}

/**
 * Batch-read param values. Each name is checked against the param map and
 * warned individually, so a typo'd tag says which one it was rather than
 * becoming a silent undefined in a list.
 */
export function readParamValues(
  configurator: any,
  initialized: boolean,
  namesOrMap: string[] | Record<string, string>,
): any[] | Record<string, any> {
  if (!initialized) {
    console.warn('[as-configurator] getParamValues called before initialization');
    if (Array.isArray(namesOrMap)) return namesOrMap.map(() => undefined);
    return Object.fromEntries(Object.keys(namesOrMap).map(alias => [alias, undefined]));
  }

  const resolve = (paramName: string): any => {
    if (!lookupParam(configurator.params, paramName)) {
      console.warn(`[as-configurator] getParamValues: no param found for "${paramName}"`);
    }
    return configurator.getParamValue(paramName);
  };

  if (Array.isArray(namesOrMap)) return namesOrMap.map(resolve);

  const out: Record<string, any> = {};
  for (const [alias, paramName] of Object.entries(namesOrMap)) {
    out[alias] = resolve(paramName);
  }
  return out;
}
